import moment from 'moment';
import { doesDocumentExtendExpiration, } from '../../../documents/index.js';
import { objectBy } from '../../../util/index.js';
import { FixedDistributionAccount, appliesTo, } from '../../fixed-distributions/index.js';
import { ImposeInterestFrequency, VoucherTypeIds, VoucherTypeSetting, daysAYear, } from '../../vouchers/index.js';
import { CaseVoucherInterestSettings } from '../../vouchers/setup.js';
import { adjustEarmarkReservations, getUnallocatedBalance, } from '../distributor/index.js';
import { calculateV2 } from '../engine-v2/index.js';
import helpers from '../index.js';
import { isEngineV2 } from '../policy.js';
import { addDatesToCaseVoucher, clone, parseDate, sortBySameCategoryDateDescFirst, sortByWithExtraDiscriminator, toISODate, } from '../utils.js';
import { interruptsLimitationPeriod } from '../vouchers.js';
import { COMPOUNDING_PERIOD_TO_BREAKPOINT_TYPE, ONE_DAY_IN_MS, addAmountToVoucher, getInterestRatesGen, getMonthStartBreakpoints, getOrderedBreakpoints, startedMonthsBetween, withBreakpoints, } from './common.js';
import { COST_CEILING_DISABLED, createCostCeilingLedger, } from './cost-ceiling.js';
import { addParsedInterestPeriods, appendShadowHist, applyShadowLump, initShadowInterestStates, overlayInterestPeriods, } from './periods.js';
export { COST_CEILING_DISABLED, getCostCeiling, getCostCeilingSummary, } from './cost-ceiling.js';
export const NO_CASE_CONDITIONS = {
    costCeiling: COST_CEILING_DISABLED,
};
export { formatInterestRate, getCurrentInterest, getLowestCaseInterestRate, } from './common.js';
const interestOrderHashMap = {
    cost: 1,
    courtcost: 2,
    interest: 3,
    principal: 4,
};
const computeInterestAmount = (caseVoucher, isStartedMonthImpose, accrualBasis, rate, from, to) => {
    if (isStartedMonthImpose) {
        const months = startedMonthsBetween(from, to);
        return Math.round(accrualBasis * rate) * months;
    }
    const interestDays = (to - from) / ONE_DAY_IN_MS;
    if (interestDays % 1 !== 0) {
        throw new Error('Number of interest days cannot be with decimal places');
    }
    const interestPerDay = Math.round((accrualBasis * rate) / daysAYear(caseVoucher));
    return interestPerDay * interestDays;
};
export const calculate = async (caseVouchers, documents, policy, fixedDistributions, untilISOString, options) => {
    if (isEngineV2(policy)) {
        return calculateV2(caseVouchers, documents, policy, fixedDistributions, untilISOString, options);
    }
    const { appendHist, postProcessing, c } = options;
    const start = performance.now();
    const until = parseDate(untilISOString);
    const _caseVouchers = helpers.vouchers
        .postProcessing(caseVouchers, {
        includeIrrecoverableVouchers: postProcessing?.filter?.includeIrrecoverableVouchers || false,
        includeDeletedVouchers: postProcessing?.filter?.includeDeletedVouchers || false,
        includeExpiredVouchers: postProcessing?.filter?.includeExpiredVouchers || false,
    })
        .map(addDatesToCaseVoucher)
        .map((voucher) => addParsedInterestPeriods(voucher));
    let filters = [
        sortBySameCategoryDateDescFirst,
        (a, b) => (interestOrderHashMap[a.category] || 0) -
            (interestOrderHashMap[b.category] || 0),
    ];
    const dateFilter = (a, b) => {
        return a.rawDate - b.rawDate;
    };
    const allBreakpoints = options.monthStartBreakpoints
        ? getOrderedBreakpoints(_caseVouchers, until)
            .concat(getMonthStartBreakpoints(_caseVouchers, until))
            .sort((a, b) => a.date - b.date)
        : getOrderedBreakpoints(_caseVouchers, until);
    const expirationCheckpoints = collectExpirationCheckpoints(_caseVouchers, documents);
    const validSpans = buildValidSpans(expirationCheckpoints, policy, until);
    // Reapplied interest is only acknowledged by checkpoints at/after voidedAt.
    const restrictedValidSpansCache = {};
    const getRestrictedValidSpans = (voidedAt) => {
        let spans = restrictedValidSpansCache[voidedAt];
        if (spans === undefined) {
            spans = buildValidSpans(expirationCheckpoints.filter((checkpoint) => checkpoint >= voidedAt), policy, until);
            restrictedValidSpansCache[voidedAt] = spans;
        }
        return spans;
    };
    filters = filters.concat([dateFilter, (a, b) => a.id - b.id]);
    const caseVoucherLookup = objectBy(_caseVouchers, (cv) => cv.caseVoucherId);
    const walkVouchers = clone(_caseVouchers).map((v) => {
        if (v.setting && CaseVoucherInterestSettings.includes(v.setting)) {
            v.amount = 0;
            const shadowInterestStates = initShadowInterestStates(v);
            if (shadowInterestStates !== undefined) {
                v.shadowInterestStates = shadowInterestStates;
            }
            // Handle legacy interest expiration, can hopefully be removed in the future
            if (v.details?.legacyInterestExpiration &&
                v.interestStartDate &&
                !v.interestEndDate) {
                const interestEndDate = moment(until).subtract(3, 'years');
                if (moment(v.interestStartDate).isBefore(interestEndDate)) {
                    v.interestStartDate = interestEndDate.format();
                    v.parsedInterestStartDate = v.interestStartDate
                        ? parseDate(toISODate(v.interestStartDate))
                        : null;
                }
            }
        }
        return v;
    });
    const costCeilingLedger = createCostCeilingLedger(_caseVouchers, options.costCeiling);
    const distributed = await helpers.distributor.distribute(walkVouchers, sortByWithExtraDiscriminator(...filters), fixedDistributions.filter(appliesTo(FixedDistributionAccount.Debtor)), {
        buildDistributionEarmarks: buildReferenceVoucherEarmarks,
        modifier: async (_currState, _prevState, voucher, nextVoucher) => {
            // prevState is never changed from _prevstate, so can be saved at this level
            let prevLookup = null; //objectBy(prevState, (cv) => cv.caseVoucherId)
            return withBreakpoints(allBreakpoints, until, _currState, _prevState, voucher, nextVoucher, (currState, prevState, breakpointFrom, breakpointTo, breakpointToType) => {
                for (let i = 0; i < currState.length; i++) {
                    const interestCaseVoucher = currState[i];
                    if (interestCaseVoucher.deletedAt ||
                        !(interestCaseVoucher.setting &&
                            CaseVoucherInterestSettings.includes(interestCaseVoucher.setting))) {
                        continue;
                    }
                    if (!interestCaseVoucher.interestStartDate) {
                        throw new Error('No interest start date on interest');
                    }
                    const interestStartDate = interestCaseVoucher.parsedInterestStartDate;
                    const interestEndDate = interestCaseVoucher.parsedInterestEndDate;
                    const shadowStates = interestCaseVoucher.shadowInterestStates;
                    if (shadowStates != null) {
                        for (const periodId in shadowStates) {
                            const shadow = shadowStates[periodId];
                            if (!shadow.applied && breakpointFrom >= shadow.voidedAt) {
                                applyShadowLump(interestCaseVoucher, shadow, appendHist);
                                costCeilingLedger?.record(interestCaseVoucher, shadow.hypExtraAmount);
                            }
                        }
                    }
                    // Make sure interest is active in period
                    // TODO: There is a breaktime on interest start and end so no need to
                    if (!(interestStartDate < breakpointTo &&
                        (!interestEndDate ||
                            (interestEndDate && interestEndDate > breakpointFrom)) &&
                        until > interestStartDate)) {
                        continue;
                    }
                    // Find from date by looking at interestStartDate, until
                    let from;
                    if (breakpointFrom < interestStartDate) {
                        from = interestStartDate;
                    }
                    else {
                        from = breakpointFrom;
                    }
                    // Find to date
                    let to;
                    const voucherTo = interestEndDate && interestEndDate < breakpointTo
                        ? interestEndDate
                        : breakpointTo;
                    if (until < voucherTo) {
                        to = until;
                    }
                    else {
                        to = voucherTo;
                    }
                    // Validate period
                    if (from > to) {
                        throw new Error('Interest period is invalid');
                    }
                    if (prevLookup == null) {
                        prevLookup = {};
                        for (const cv of prevState) {
                            prevLookup[cv.caseVoucherId] = cv;
                        }
                    }
                    const refDebt = interestCaseVoucher.referenceCaseVoucherId
                        ? prevLookup[interestCaseVoucher.referenceCaseVoucherId]
                        : undefined;
                    const refDebtFromVouchers = !refDebt && interestCaseVoucher.referenceCaseVoucherId
                        ? caseVoucherLookup[interestCaseVoucher.referenceCaseVoucherId]
                        : undefined;
                    const refAmount = ((refDebt
                        ? refDebt.amount
                        : refDebtFromVouchers
                            ? refDebtFromVouchers.amount
                            : 0) || 0) - (refDebt ? refDebt.distributionAmount : 0);
                    let capitalizedInterestAmount = 0;
                    if (interestCaseVoucher.setting ===
                        VoucherTypeSetting.CompoundInterest) {
                        // Compute capitalized interest amount
                        // Transactions should pay the uncapitalized interest first
                        capitalizedInterestAmount =
                            interestCaseVoucher.amountAtCapitalization || 0;
                        const uncapitalizedInterestAmount = interestCaseVoucher.amount - capitalizedInterestAmount;
                        const amountToSubractFromCapitalizedAmount = uncapitalizedInterestAmount -
                            interestCaseVoucher.distributionAmount;
                        if (amountToSubractFromCapitalizedAmount > 0) {
                            capitalizedInterestAmount +=
                                amountToSubractFromCapitalizedAmount;
                        }
                        // Find fixed interest vouchers and add to capitalized amount
                        let fixedInterestAmount = 0;
                        for (const v of currState) {
                            if (v.voucherTypeId === VoucherTypeIds.FIXEDINTEREST &&
                                v.referenceCaseVoucherId ===
                                    interestCaseVoucher.referenceCaseVoucherId) {
                                fixedInterestAmount += v.amount - v.distributionAmount;
                            }
                        }
                        capitalizedInterestAmount += fixedInterestAmount;
                    }
                    // Find amount
                    let interests;
                    if (interestCaseVoucher.setting ===
                        VoucherTypeSetting.InterestFromTable) {
                        // TODO: Slow?
                        interests = getInterestRatesGen(interestCaseVoucher.settingDetails.interestRates)(from, to);
                    }
                    else if ((interestCaseVoucher.setting ===
                        VoucherTypeSetting.InterestPercentage ||
                        interestCaseVoucher.setting ===
                            VoucherTypeSetting.CompoundInterest) &&
                        typeof interestCaseVoucher.interest === 'number') {
                        interests = [{ rate: interestCaseVoucher.interest }];
                    }
                    else {
                        throw new Error(`Interest type not handled (setting: ${interestCaseVoucher.setting}, voucher id: ${interestCaseVoucher.caseVoucherId})`);
                    }
                    const parsedInterestPeriods = interestCaseVoucher.parsedInterestPeriods;
                    if (parsedInterestPeriods != null &&
                        parsedInterestPeriods.length > 0) {
                        interests = overlayInterestPeriods(interests, parsedInterestPeriods, from, to);
                    }
                    const preventExpiration = c?.preventInterestExpiration ||
                        interestCaseVoucher.details?.preventInterestExpiration ||
                        (interestCaseVoucher.interestStartDate != null &&
                            interestCaseVoucher.interestEndDate != null);
                    const isCompoundingBreakpoint = interestCaseVoucher.setting ===
                        VoucherTypeSetting.CompoundInterest &&
                        COMPOUNDING_PERIOD_TO_BREAKPOINT_TYPE[interestCaseVoucher.settingDetails.compoundingPeriod] === breakpointToType;
                    // Per Danish accounting convention "renter per påbegyndt måned":
                    // any month the period touches counts as a full month, and the
                    // stored rate is itself the monthly rate (no /12 divisor).
                    const isStartedMonthImpose = interestCaseVoucher.setting ===
                        VoucherTypeSetting.InterestPercentage &&
                        interestCaseVoucher.settingDetails.imposeInterestFrequency ===
                            ImposeInterestFrequency.Monthly;
                    for (let g = 0; g < interests.length; g++) {
                        const interestPeriod = interests[g];
                        const nextInterestPeriod = interests[g + 1];
                        const interestPeriodFrom = interestPeriod.time && interestPeriod.time >= from
                            ? interestPeriod.time
                            : from;
                        const interestPeriodTo = nextInterestPeriod &&
                            nextInterestPeriod.time &&
                            nextInterestPeriod.time <= to
                            ? nextInterestPeriod.time
                            : to;
                        const voidedShadow = interestPeriod.voidedPeriodId != null && shadowStates != null
                            ? shadowStates[interestPeriod.voidedPeriodId]
                            : undefined;
                        const accrualBasis = refAmount + capitalizedInterestAmount;
                        const spans = preventExpiration
                            ? [[interestPeriodFrom, interestPeriodTo]]
                            : calculateOverlap(validSpans, [
                                interestPeriodFrom,
                                interestPeriodTo,
                            ]);
                        for (const [spanFrom, spanTo] of spans) {
                            // Voided daily period: charge the full span but show the
                            // reduced rate only where the waived part expired. Started-month
                            // takes the else path (can't split a month on day boundaries).
                            if (voidedShadow != null && !isStartedMonthImpose) {
                                const inRestricted = preventExpiration
                                    ? [[spanFrom, spanTo]]
                                    : calculateOverlap(getRestrictedValidSpans(voidedShadow.voidedAt), [spanFrom, spanTo]);
                                const notRestricted = subtractSpans(spanFrom, spanTo, inRestricted);
                                for (const [f, t] of inRestricted) {
                                    const chargedAmount = computeInterestAmount(interestCaseVoucher, isStartedMonthImpose, accrualBasis, interestPeriod.rate, f, t);
                                    currState[i] = addAmountToVoucher(interestCaseVoucher, chargedAmount, undefined);
                                    costCeilingLedger?.record(interestCaseVoucher, chargedAmount);
                                }
                                for (const [f, t] of notRestricted) {
                                    const reducedAmount = computeInterestAmount(interestCaseVoucher, isStartedMonthImpose, accrualBasis, interestPeriod.rate, f, t);
                                    currState[i] = addAmountToVoucher(interestCaseVoucher, reducedAmount, appendHist && reducedAmount !== 0
                                        ? {
                                            from: f,
                                            to: t,
                                            interest: interestPeriod.rate,
                                            referenceVoucherBalance: refAmount,
                                            capitalizedInterestBalance: capitalizedInterestAmount,
                                        }
                                        : undefined);
                                    costCeilingLedger?.record(interestCaseVoucher, reducedAmount);
                                }
                            }
                            else {
                                const amountToBeAdded = computeInterestAmount(interestCaseVoucher, isStartedMonthImpose, accrualBasis, interestPeriod.rate, spanFrom, spanTo);
                                // Started-month voided freeze: the 0 row is superseded by
                                // the reapplied row.
                                const isOverriddenFrozenRow = interestPeriod.voidedPeriodId != null &&
                                    amountToBeAdded === 0;
                                currState[i] = addAmountToVoucher(interestCaseVoucher, amountToBeAdded, appendHist && !isOverriddenFrozenRow
                                    ? {
                                        from: spanFrom,
                                        to: spanTo,
                                        interest: interestPeriod.rate,
                                        referenceVoucherBalance: refAmount,
                                        capitalizedInterestBalance: capitalizedInterestAmount,
                                    }
                                    : undefined);
                                costCeilingLedger?.record(interestCaseVoucher, amountToBeAdded);
                            }
                            if (isCompoundingBreakpoint) {
                                interestCaseVoucher.amountAtCapitalization =
                                    interestCaseVoucher.amount;
                            }
                        }
                        // Shadow track: accrue the waived part on the actual balances,
                        // expiring against the restricted spans.
                        if (shadowStates != null) {
                            for (const periodId in shadowStates) {
                                const shadow = shadowStates[periodId];
                                if (shadow.applied ||
                                    interestPeriodFrom >= shadow.voidedAt) {
                                    continue;
                                }
                                const shadowTo = interestPeriodTo < shadow.voidedAt
                                    ? interestPeriodTo
                                    : shadow.voidedAt;
                                if (interestPeriodFrom >= shadowTo) {
                                    continue;
                                }
                                // Base rate inside the period's own window, effective rate
                                // elsewhere.
                                const hypRate = interestPeriod.voidedPeriodId === periodId
                                    ? (interestPeriod.baseRate ?? interestPeriod.rate)
                                    : interestPeriod.rate;
                                const hypBasis = refAmount +
                                    capitalizedInterestAmount +
                                    shadow.hypExtraCapitalized;
                                const actualBasis = refAmount + capitalizedInterestAmount;
                                const shadowSpans = preventExpiration
                                    ? [[interestPeriodFrom, shadowTo]]
                                    : calculateOverlap(getRestrictedValidSpans(shadow.voidedAt), [interestPeriodFrom, shadowTo]);
                                for (const [shadowFrom, shadowSpanTo] of shadowSpans) {
                                    // diff = waived part (the reapply lump); full = the whole
                                    // base-rate accrual, as if the period never happened.
                                    let diffAmount;
                                    let fullAmount;
                                    if (isStartedMonthImpose) {
                                        const months = startedMonthsBetween(shadowFrom, shadowSpanTo);
                                        const fullPerMonth = Math.round(hypBasis * hypRate);
                                        const actualPerMonth = Math.round(actualBasis * interestPeriod.rate);
                                        diffAmount = (fullPerMonth - actualPerMonth) * months;
                                        fullAmount = fullPerMonth * months;
                                    }
                                    else {
                                        const shadowDays = (shadowSpanTo - shadowFrom) / ONE_DAY_IN_MS;
                                        const interestDaysAYear = daysAYear(interestCaseVoucher);
                                        const fullPerDay = Math.round((hypBasis * hypRate) / interestDaysAYear);
                                        const actualPerDay = Math.round((actualBasis * interestPeriod.rate) /
                                            interestDaysAYear);
                                        diffAmount = (fullPerDay - actualPerDay) * shadowDays;
                                        fullAmount = fullPerDay * shadowDays;
                                    }
                                    if (diffAmount !== 0) {
                                        shadow.hypExtraAmount += diffAmount;
                                    }
                                    // The period's own daily window suppressed the charged
                                    // row, so show the full reconstruction; elsewhere only the
                                    // waived difference.
                                    const showFullReconstruction = !isStartedMonthImpose &&
                                        interestPeriod.voidedPeriodId === periodId;
                                    const histAmount = showFullReconstruction
                                        ? fullAmount
                                        : diffAmount;
                                    if (appendHist && histAmount !== 0) {
                                        appendShadowHist(shadow, {
                                            from: shadowFrom,
                                            to: shadowSpanTo,
                                            amount: histAmount,
                                            interest: hypRate,
                                            referenceVoucherBalance: refAmount,
                                            capitalizedInterestBalance: interestCaseVoucher.setting ===
                                                VoucherTypeSetting.CompoundInterest
                                                ? capitalizedInterestAmount +
                                                    shadow.hypExtraCapitalized
                                                : undefined,
                                        });
                                    }
                                }
                            }
                        }
                    }
                    if (shadowStates != null && isCompoundingBreakpoint) {
                        for (const periodId in shadowStates) {
                            const shadow = shadowStates[periodId];
                            if (!shadow.applied) {
                                shadow.hypExtraCapitalized = shadow.hypExtraAmount;
                            }
                        }
                    }
                }
                costCeilingLedger?.settle();
                if (options.breakPointCallBack) {
                    options.breakPointCallBack(currState, breakpointFrom, breakpointTo);
                }
                return currState;
            });
        },
    });
    // Voids at/after the last replay segment never crossed a breakpoint; apply
    // their lumps here.
    for (const voucher of distributed) {
        if (voucher.shadowInterestStates != null) {
            for (const periodId in voucher.shadowInterestStates) {
                const shadow = voucher.shadowInterestStates[periodId];
                if (!shadow.applied && shadow.voidedAt <= until) {
                    applyShadowLump(voucher, shadow, appendHist);
                    costCeilingLedger?.record(voucher, shadow.hypExtraAmount);
                }
            }
        }
    }
    costCeilingLedger?.settle();
    costCeilingLedger?.finalize();
    const end = performance.now();
    if (end - start > 10) {
        console.log('Interest calculation time:', end - start);
    }
    return distributed;
};
export const changes = (vouchers) => {
    const _changes = [];
    for (let i = 0; i < vouchers.length; i++) {
        vouchers[i].amountUpdated &&
            _changes.push({
                amount: vouchers[i].amount,
                caseVoucherId: vouchers[i].caseVoucherId,
            });
    }
    return _changes;
};
const collectExpirationCheckpoints = (caseVouchers, documents) => {
    const expirationCheckpoints = [];
    for (const document of documents) {
        if (doesDocumentExtendExpiration(document)) {
            expirationCheckpoints.push(new Date(document.signedAt ?? document.date).getTime());
        }
    }
    for (const voucher of caseVouchers) {
        if (interruptsLimitationPeriod(voucher)) {
            expirationCheckpoints.push(new Date(voucher.date).getTime());
        }
    }
    return expirationCheckpoints;
};
const buildValidSpans = (checkpoints, settings, until) => {
    const expirationCheckpoints = checkpoints.concat(until);
    expirationCheckpoints.sort((a, b) => a - b);
    let lockedPeriods = [];
    for (const checkpoint of expirationCheckpoints) {
        const start = moment(checkpoint)
            .subtract(settings.trailingYears ?? 3, 'years')
            .utc()
            .startOf('day')
            .valueOf();
        const end = moment(checkpoint).utc().startOf('day').valueOf();
        if (lockedPeriods.length === 0) {
            lockedPeriods.push([start, end]);
            continue;
        }
        const latest = lockedPeriods[lockedPeriods.length - 1];
        if (start <= latest[1]) {
            latest[1] = end;
        }
        else {
            lockedPeriods.push([start, end]);
        }
    }
    return lockedPeriods;
};
const calculateOverlap = (validSpans, attempt) => {
    let neu = [];
    for (const span of validSpans) {
        if (attempt[0] < span[0] && attempt[1] > span[0]) {
            // Starts before, but ends after start
            if (attempt[1] > span[1]) {
                // Ends after span
                neu.push([span[0], span[1]]);
            }
            else {
                // Ends within span
                neu.push([span[0], attempt[1]]);
            }
        }
        if (attempt[0] >= span[0] && attempt[0] < span[1]) {
            // Starts within span
            if (attempt[1] > span[1]) {
                // Ends after span
                neu.push([attempt[0], span[1]]);
            }
            else {
                // Ends within span
                neu.push([attempt[0], attempt[1]]);
            }
        }
    }
    return neu;
};
// `covered` need not be sorted; returned gaps are clamped to [from, to].
export const subtractSpans = (from, to, covered) => {
    const sorted = covered
        .map((span) => [
        Math.max(from, span[0]),
        Math.min(to, span[1]),
    ])
        .filter((span) => span[0] < span[1])
        .sort((a, b) => a[0] - b[0]);
    const gaps = [];
    let cursor = from;
    for (const [coveredFrom, coveredTo] of sorted) {
        if (coveredFrom > cursor) {
            gaps.push([cursor, coveredFrom]);
        }
        if (coveredTo > cursor) {
            cursor = coveredTo;
        }
    }
    if (cursor < to) {
        gaps.push([cursor, to]);
    }
    return gaps;
};
export const buildReferenceVoucherEarmarks = (caseVouchers, reservations) => {
    const vouchersById = objectBy(caseVouchers, (voucher) => voucher.caseVoucherId);
    const workingReservations = new Map(reservations);
    const earmarks = [];
    for (const sourceVoucher of caseVouchers) {
        if (sourceVoucher.amount <= 0 ||
            sourceVoucher.referenceCaseVoucherId == null) {
            continue;
        }
        const referencedVoucher = vouchersById[sourceVoucher.referenceCaseVoucherId];
        if (referencedVoucher === undefined || referencedVoucher.amount > 0) {
            console.error(`[Reference distribution]: Could not find matching negative voucher for referenceId ${sourceVoucher.referenceCaseVoucherId} on voucher ${sourceVoucher.caseVoucherId}`);
            continue;
        }
        const availableSourceAmount = -getUnallocatedBalance(sourceVoucher, workingReservations);
        const availableReferencedAmount = getUnallocatedBalance(referencedVoucher, workingReservations);
        if (availableSourceAmount <= 0 || availableReferencedAmount <= 0) {
            continue;
        }
        const amount = Math.min(availableSourceAmount, availableReferencedAmount);
        const earmark = {
            sourceVoucherId: sourceVoucher.caseVoucherId,
            targetVoucherId: referencedVoucher.caseVoucherId,
            amount,
        };
        earmarks.push(earmark);
        // Hold the capacity so a later reference cannot claim the same amount
        adjustEarmarkReservations(workingReservations, [earmark], 1);
    }
    return earmarks;
};
//# sourceMappingURL=index.js.map